/**
 * Stripe embedded Connect components (ADR 0044) — onboarding + the
 * requirements notification banner, rendered INSIDE the admin (no redirect to
 * stripe.com). Sessions come from POST /organizations/:id/stripeAccountSession.
 */
import React, { useState } from 'react';
import { loadConnectAndInitialize, StripeConnectInstance } from '@stripe/connect-js';
import {
  ConnectAccountOnboarding,
  ConnectComponentsProvider,
  ConnectNotificationBanner,
} from '@stripe/react-connect-js';

export interface AccountSession {
  clientSecret: string;
  publishableKey: string;
  accountId: string;
}

interface InstanceProps {
  session: AccountSession;
  /** Mint a fresh session when the first client secret expires. */
  refreshClientSecret: () => Promise<string | null>;
}

// ONE ConnectInstance per client secret, cached for the page's lifetime. An
// Account Session client secret is SINGLE-USE: initializing connect-js with
// the same secret twice claims the session twice and Stripe rejects the
// second claim ("already been claimed"), which the onboarding component
// surfaces as "There was an error during authentication". React is allowed to
// execute a mount render more than once (concurrent re-render in prod — how
// this bit on dash.staty.io — and StrictMode in dev), so creation must be
// idempotent per secret, not per render.
const instancesBySecret = new Map<string, StripeConnectInstance>();

function getConnectInstance({ session, refreshClientSecret }: InstanceProps): StripeConnectInstance {
  const existing = instancesBySecret.get(session.clientSecret);
  if (existing) return existing;
  // The initial secret is used exactly once; expiry re-mints via the API.
  let initial: string | null = session.clientSecret;
  const instance = loadConnectAndInitialize({
    publishableKey: session.publishableKey,
    fetchClientSecret: async () => {
      if (initial) {
        const secret = initial;
        initial = null;
        return secret;
      }
      const next = await refreshClientSecret();
      if (!next) throw new Error('Could not refresh the Stripe session');
      return next;
    },
    appearance: {
      variables: { colorPrimary: '#2563eb', borderRadius: '8px' },
    },
  });
  instancesBySecret.set(session.clientSecret, instance);
  return instance;
}

function useConnectInstance(props: InstanceProps): StripeConnectInstance {
  const [instance] = useState<StripeConnectInstance>(() => getConnectInstance(props));
  return instance;
}

export default function StripeEmbeddedOnboarding({
  session,
  refreshClientSecret,
  onExit,
}: InstanceProps & { onExit: () => void }) {
  const instance = useConnectInstance({ session, refreshClientSecret });
  return (
    <ConnectComponentsProvider connectInstance={instance}>
      <ConnectAccountOnboarding
        onExit={onExit}
        // Ask only what's needed to start charging; Stripe collects the rest
        // later (payout-volume thresholds) via the notification banner.
        collectionOptions={{ fields: 'currently_due', futureRequirements: 'omit' }}
      />
    </ConnectComponentsProvider>
  );
}

/** "Action needed" banner for connected orgs — renders nothing when the
 *  account is healthy (Stripe controls visibility). */
export function StripeRequirementsBanner({ session, refreshClientSecret }: InstanceProps) {
  const instance = useConnectInstance({ session, refreshClientSecret });
  return (
    <ConnectComponentsProvider connectInstance={instance}>
      <ConnectNotificationBanner />
    </ConnectComponentsProvider>
  );
}

/**
 * Self-fetching banner wrapper for dashboards: mints its own account session.
 * Mount it ONLY when the org has outstanding requirements (the caller gates on
 * stripeStatus.requirementsDue) so healthy orgs never pay the session call.
 */
export function StripeRequirementsBannerContainer({
  orgId,
  createStripeAccountSession,
}: {
  orgId: string;
  createStripeAccountSession: (orgId: string, payload: object) => Promise<AccountSession | null>;
}) {
  const [session, setSession] = useState<AccountSession | null>(null);
  React.useEffect(() => {
    let active = true;
    void createStripeAccountSession(orgId, {}).then((result) => {
      if (active && result?.clientSecret) setSession(result);
    });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  if (!session) return null;
  return (
    <StripeRequirementsBanner
      session={session}
      refreshClientSecret={async () =>
        (await createStripeAccountSession(orgId, {}))?.clientSecret ?? null
      }
    />
  );
}
